import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellPlus, CalendarClock, Check, Trash2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { rappelsApi } from "@/lib/crmApi";
import { formatDateTime, isOverdue } from "@/lib/crmHelpers";

export default function RappelsSection({ leadId }) {
  const [rappels, setRappels] = useState([]);
  const [dateHeure, setDateHeure] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      const data = await rappelsApi.list(leadId);
      setRappels(data || []);
    } catch (e) {
      /* ignore */
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!dateHeure || !message.trim()) {
      toast.error("Date et message requis");
      return;
    }
    setLoading(true);
    try {
      await rappelsApi.create({
        lead_id: leadId,
        date_heure: new Date(dateHeure).toISOString(),
        message: message.trim(),
      });
      toast.success("Rappel planifié");
      setDateHeure("");
      setMessage("");
      load();
    } catch (e) {
      toast.error("Erreur de création");
    } finally {
      setLoading(false);
    }
  };

  const toggleDone = async (r) => {
    await rappelsApi.update(r.id, { fait: !r.fait });
    load();
  };

  const remove = async (r) => {
    if (!window.confirm("Supprimer ce rappel ?")) return;
    await rappelsApi.remove(r.id);
    toast.success("Rappel supprimé");
    load();
  };

  const pending = rappels.filter((r) => !r.fait);
  const done = rappels.filter((r) => r.fait);

  return (
    <div data-testid="rappels-section" className="space-y-4">
      <div className="rounded-lg border border-border/60 bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <BellPlus className="h-4 w-4" />
          <span className="font-heading text-sm font-semibold">
            Planifier un rappel
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-[200px_1fr_auto]">
          <Input
            type="datetime-local"
            value={dateHeure}
            onChange={(e) => setDateHeure(e.target.value)}
            data-testid="rappel-date"
          />
          <Input
            placeholder="Ex: Rappeler pour confirmer RDV..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="rappel-message"
          />
          <Button
            onClick={handleCreate}
            disabled={loading}
            data-testid="rappel-submit"
            className="gap-2"
          >
            <BellPlus className="h-4 w-4" />
            Ajouter
          </Button>
        </div>
      </div>

      <div>
        <div className="text-label mb-2 text-muted-foreground">
          À venir ({pending.length})
        </div>
        {pending.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border/60 py-6 text-center text-sm text-muted-foreground">
            Aucun rappel planifié.
          </p>
        ) : (
          <div className="space-y-2">
            {pending.map((r) => {
              const overdue = isOverdue(r.date_heure);
              return (
                <div
                  key={r.id}
                  data-testid={`rappel-${r.id}`}
                  className={`flex items-start gap-3 rounded-lg border p-3 ${
                    overdue
                      ? "border-red-200 bg-red-50/40 dark:border-red-900/40 dark:bg-red-950/20"
                      : "border-border/60 bg-card"
                  }`}
                >
                  <CalendarClock className={`mt-0.5 h-4 w-4 flex-none ${overdue ? "text-red-500" : "text-muted-foreground"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{r.message}</div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span>{formatDateTime(r.date_heure)}</span>
                      {overdue && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-950/60 dark:text-red-300">
                          <AlertCircle className="h-2.5 w-2.5" />
                          En retard
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleDone(r)}
                    data-testid={`rappel-done-${r.id}`}
                    className="h-7 w-7 p-0"
                    title="Marquer fait"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(r)}
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500"
                    title="Supprimer"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {done.length > 0 && (
        <div>
          <div className="text-label mb-2 text-muted-foreground">
            Terminés ({done.length})
          </div>
          <div className="space-y-1.5">
            {done.slice(0, 10).map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-2 rounded-md px-2 py-1 text-xs text-muted-foreground"
              >
                <Check className="h-3 w-3 text-emerald-500" />
                <span className="line-through">{r.message}</span>
                <span>· {formatDateTime(r.date_heure)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
